// src/hooks/useTourNudge.js
// Page-visit trigger for the tour auto-prompt nudge.
//
// Logic:
//   - Reads the current pathname via react-router useLocation.
//   - Maps the pathname to a tourId from a static table (UI-SPEC "Tour Nudge Copy" table).
//     On /templates the tour follows the ?tab= on screen (C-WR-06).
//   - Returns { tourId, nudgeBody, visible, dismiss } where:
//       visible = true ONLY when tourId is non-null AND isNudgeSuppressed(tourId) is false.
//       dismiss() is an async, durable per-tour dismiss: it hides the popover immediately,
//                 then writes markSeen(tourId, 'dismissed') to the users/{uid} document
//                 through TourContext's onTourStateUpdate. There is no session-only path.
//
// Dismissal is per-tour, not global (D-01): silencing the nudge on one page (e.g.
// /templates) leaves every other mapped page's nudge intact, because the write is
// keyed by `tourState.{tourId}` and isNudgeSuppressed reads that same per-tour entry.
//
// This reverses the prior D-21/D-23 framing, which made dismissal a tab-local, browser-tab-only
// flag that never survived a reload of the same tab. Under Phase 42's D-02 and D-05, Dismiss
// now writes durably, and the old tab-local dismiss mechanism is removed entirely rather than
// deprecated in place.

import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTour } from '../components/features/tours/TourContext';
import { templatesTourIdForTab } from '../components/features/tours/tourRoute';
import { TEMPLATES_PATH } from '../utils/templatesNavigation';
import { CRM_PATH, LEGACY_CONTACTS_PATH } from '../components/ui/navigationConfig';

// ---- Static pathname → tourId map ----
// Source: UI-SPEC "Tour Nudge Copy (per page)" table + Guides List grouping.
// Exact-match on pathname; no regex. Paths must match the canonical routes from
// navigationConfig.PATH_FOR_VIEW / getViewFromPathname to avoid silent no-ops.

export const PATHNAME_TO_TOUR_ID = {
    '/': 'welcome',
    '/dashboard': 'welcome',
    '/transactions': 'transaction-manual',
    '/transactions/kanban': 'transaction-manual',
    '/transactions/archive': 'transaction-manual',
    [CRM_PATH]: 'add-client',
    // Old path, still accepted (expand, then contract).
    [LEGACY_CONTACTS_PATH]: 'add-client',
    '/business-partners': 'partners-page',
    '/business-partners/realtors': 'partners-page',
    // The Templates page (owner decision, 2026-10-04); /email-templates only
    // redirects here now. This is the Transaction emails tab's tour, which bare /templates
    // shows; resolveNudgeTourId picks the tour for the tab actually on screen.
    '/templates': 'email-templates',
    '/automations': 'automations',
    '/settings': 'settings',
    '/manage-workspace': 'manage-team',
};

// ---- Per-tour nudge body copy ----
// Source: UI-SPEC "Tour Nudge Copy (per page)" table.
//
// D-02 orphan rule (Phase 47): the `dashboard` tour was retired into `welcome`
// (Phase 47, D-01). Legacy tourState.dashboard entries in users/{uid} are left
// orphaned deliberately, following Phase 42's D-09 precedent for
// tourState.templates — no alias, no migration. A user who already took the
// old dashboard tour is nudged once more for `welcome`, then never again.

export const NUDGE_COPY = {
    'welcome': 'New here? Take a quick tour to get oriented.',
    'transaction-manual': 'See how to create and manage transactions.',
    'add-client': 'Learn how to track clients and relationships.',
    'partners-page': 'See how to add and manage your vendor network.',
    'email-templates': 'Learn how to create and use reusable templates.',
    'checklist-templates': 'See how to build checklists you can reuse on every deal.',
    'automations': 'See how automations can save you time.',
    'settings': 'Get your workspace configured quickly.',
    'manage-team': 'See how to invite and manage team members.',
};

/**
 * The tour a page visit nudges (C-WR-06). The Templates page has one tour per
 * tab: Email nudges the Email tour, Checklists nudges the Checklists tour, and
 * Action plans has no tour, so it nudges nothing. Accepting a nudge therefore
 * never switches tabs (or drops unsaved plan edits).
 * @param {{ pathname?: string, search?: string }} location
 * @returns {string|null}
 */
export function resolveNudgeTourId(location) {
    const pathname = location?.pathname || '';
    if (pathname === TEMPLATES_PATH) {
        return templatesTourIdForTab(new URLSearchParams(location.search || '').get('tab'));
    }
    return PATHNAME_TO_TOUR_ID[pathname] ?? null;
}

export function useTourNudge() {
    const location = useLocation();
    const { isNudgeSuppressed, markSeen } = useTour();

    const tourId = resolveNudgeTourId(location);

    // Local visibility state — separate from Firestore seen-state.
    // Initialise as false; set to true only if all gates pass.
    const [visible, setVisible] = React.useState(false);

    // Recompute visibility whenever the pathname changes (page-visit trigger)
    // or the underlying suppression state changes (e.g. a dismiss elsewhere
    // in the tree).
    React.useEffect(() => {
        if (!tourId) {
            setVisible(false);
            return;
        }
        setVisible(!isNudgeSuppressed(tourId));
    }, [location.pathname, tourId, isNudgeSuppressed]);

    // dismiss() — durable per-tour dismiss (D-01/D-02). Hides the popover
    // immediately so the user does not wait on the network, then writes
    // 'dismissed' through markSeen. A rejection is caught and logged the
    // same way OnboardingWizard.js's own fire-and-forget markSeen calls are.
    const dismiss = React.useCallback(async () => {
        if (!tourId) return;
        setVisible(false);
        try {
            await markSeen(tourId, 'dismissed');
        } catch (e) {
            console.error(`dismiss(${tourId}) failed`, e);
        }
    }, [tourId, markSeen]);

    return {
        tourId,
        nudgeBody: tourId ? (NUDGE_COPY[tourId] ?? null) : null,
        visible,
        dismiss,
    };
}
